import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Stroje a elektrické nářadí - V1 DRAFT - authored for structural completeness;
 * NOT a substitute for sign-off by a Czech osoba odborně způsobilá v prevenci
 * rizik. Legal refs: NV 378/2001 Sb. (bezpečný provoz strojů a technických
 * zařízení); ČSN 33 1600 ed. 2 (revize el. nářadí); NV 272/2011 Sb. (hluk -
 * ochrana sluchu od 85 dB); NV 390/2021 Sb. (OOPP). Bilingual (cs-CZ + en-US).
 * Three chapters (kontrola před použitím | bezpečná práce | po práci) + a
 * graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const powerToolsProfile: TrainingProfile = {
  slug: 'power-tools',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Stroje a elektrické nářadí',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-kontrola',
        title: t('Kontrola před použitím', 'Pre-use check'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-kontrola',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Minuta kontroly, roky prstů', 'One minute of checking saves your fingers'),
          },
          {
            id: 'p-kontrola-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Před každým použitím zkontroluj **přívodní kabel a vidlici** (bez naříznutí a přelomů), **kryty a bezpečnostní prvky** (kryt kotouče, pojistka spouště) a **stav nástroje** (prasklý kotouč, tupý vrták). Poškozené nářadí **odstav a označ** - neopravuj improvizovaně izolepou.',
              'Before every use check the **cord and plug** (no cuts or kinks), the **guards and safety features** (disc guard, trigger lock) and the **tool bit** (cracked disc, blunt drill). Take damaged tools **out of service and label them** - no improvised tape repairs.',
            ),
          },
          {
            id: 'anim-kontrola',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'toolCheck',
            steps: [
              {
                label: t('Poškozené nářadí', 'Damaged tool'),
                say: t(
                  'Prasklý kryt nebo naříznutý přívod - vyřaď z provozu a označ.',
                  'Cracked guard or nicked cord - take it out of service and tag it.',
                ),
              },
              {
                label: t('V pořádku - můžeš pracovat', 'OK - good to go'),
                say: t(
                  'Kryt nasazen, přívod i vidlice bez poškození.',
                  'Guard on, cord and plug undamaged.',
                ),
              },
            ],
            caption: t(
              'Naříznutý kabel = nářadí odstavit. V pořádku je jen nářadí s krytem a nepoškozeným přívodem.',
              'A nicked cord means the tool comes out of service. Only a tool with its guard and an intact cord is OK.',
            ),
          },
          {
            id: 'c-kontrola-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 378/2001 Sb.** (bezpečný provoz zařízení). Elektrické nářadí podléhá **pravidelným revizím dle ČSN 33 1600 ed. 2**; na staveništi používej okruhy jištěné **proudovým chráničem (RCD)**.',
              'Legal basis: **Gov. Reg. 378/2001 Coll.** (safe operation of equipment). Power tools require **periodic inspections per ČSN 33 1600 ed. 2**; on sites use circuits protected by an **RCD**.',
            ),
          },
        ],
      },
      {
        id: 'ch-prace',
        title: t('Bezpečná práce s nářadím', 'Working safely with tools'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-hluk'],
        blocks: [
          {
            id: 'h-prace',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Ruce, oči, uši', 'Hands, eyes, ears'),
          },
          {
            id: 'p-prace-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Upni obrobek** (svěrák, svěrky) - nikdy neřež ani nevrtej do dílu drženého v ruce či o koleno. Používej **brýle** při broušení a sekání a **chrániče sluchu** všude, kde hluk přesahuje **85 dB** (bruska, příklep, okružní pila). Kabel veď **za nářadím**, mimo dráhu řezu.',
              '**Clamp the workpiece** (vice, clamps) - never cut or drill into a part held in your hand or against your knee. Wear **goggles** when grinding or chiselling and **hearing protection** wherever noise exceeds **85 dB** (grinder, hammer drill, circular saw). Route the cord **behind the tool**, clear of the cut line.',
            ),
          },
          {
            id: 'kc-hluk',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Od jaké hladiny hluku musíš nosit chrániče sluchu?',
              'From what noise level must you wear hearing protection?',
            ),
            choices: [
              { id: 'kc-a', label: t('85 dB', '85 dB') },
              { id: 'kc-b', label: t('100 dB', '100 dB') },
              { id: 'kc-c', label: t('Až když to píská v uších', 'Only once your ears ring') },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'NV 272/2011: od 85 dB je zaměstnavatel povinen ochranu sluchu zajistit a používat. Poškození sluchu je nevratné.',
              'Reg. 272/2011: from 85 dB hearing protection is mandatory. Hearing damage is irreversible.',
            ),
          },
          {
            id: 'c-prace-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Vibrace**: dlouhá práce s bruskou či bouracím kladivem poškozuje cévy a nervy rukou (syndrom bílých prstů). Střídej činnosti a dělej přestávky.',
              '**Vibration**: long sessions with a grinder or breaker damage the blood vessels and nerves of the hands (white-finger syndrome). Rotate tasks and take breaks.',
            ),
          },
        ],
      },
      {
        id: 'ch-po-praci',
        title: t('Výměna nástrojů a po práci', 'Changing bits and after work'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-vymena'],
        blocks: [
          {
            id: 'h-po-praci',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Nejdřív ze zásuvky', 'Unplug first'),
          },
          {
            id: 'ord-vymena',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď bezpečnou výměnu kotouče úhlové brusky.',
              'Order a safe grinder-disc change.',
            ),
            items: [
              {
                id: 'ov-1',
                label: t(
                  'Odpoj nářadí od sítě / vyjmi akumulátor',
                  'Unplug the tool / remove the battery',
                ),
              },
              {
                id: 'ov-2',
                label: t(
                  'Počkej na úplné zastavení kotouče',
                  'Wait for the disc to stop completely',
                ),
              },
              {
                id: 'ov-3',
                label: t(
                  'Vyměň kotouč a zkontroluj upnutí i kryt',
                  'Change the disc, check clamping and guard',
                ),
              },
              {
                id: 'ov-4',
                label: t(
                  'Krátce odzkoušej naprázdno mimo tělo',
                  'Test-run briefly away from your body',
                ),
              },
            ],
          },
          {
            id: 'k-po-praci',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Každý zásah do nářadí (výměna, čištění, seřízení) jen **odpojené od zdroje**. Po práci nářadí ulož suché a čisté.',
              'Any intervention (bit change, cleaning, adjustment) only with the tool **disconnected from power**. Store tools dry and clean after work.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-kabel-single',
      kind: 'single',
      chapterId: 'ch-kontrola',
      prompt: t(
        'Najdeš naříznutý přívodní kabel vrtačky. Co uděláš?',
        'You find a nicked drill cord. What do you do?',
      ),
      choices: [
        { id: 'a', label: t('Omotám izolepou a pracuji dál', 'Wrap it in tape and carry on') },
        {
          id: 'b',
          label: t(
            'Nářadí odstavím a označím jako vadné',
            'Take the tool out of service and label it',
          ),
        },
        {
          id: 'c',
          label: t(
            'Dnes dodělám zakázku, zítra řeším',
            "Finish today's job, deal with it tomorrow",
          ),
        },
      ],
      correct: 'b',
    },
    {
      id: 'q-vymena-tf',
      kind: 'truefalse',
      chapterId: 'ch-po-praci',
      prompt: t(
        'Kotouč brusky lze vyměnit i se zapojeným přívodem, pokud je vypnutý spínač.',
        'A grinder disc may be changed with the cord plugged in, as long as the switch is off.',
      ),
      correct: false,
    },
    {
      id: 'q-oopp-multi',
      kind: 'multi',
      chapterId: 'ch-prace',
      prompt: t(
        'Jaké OOPP patří k práci s úhlovou bruskou? (vyber všechny správné)',
        'What PPE belongs to angle-grinder work? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Ochranné brýle nebo štít', 'Goggles or face shield') },
        { id: 'b', label: t('Chrániče sluchu', 'Hearing protection') },
        { id: 'c', label: t('Volné rukavice s třásněmi', 'Loose fringed gloves') },
        { id: 'd', label: t('Přiléhavý oděv', 'Close-fitting clothing') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-hluk-numeric',
      kind: 'numeric',
      chapterId: 'ch-prace',
      prompt: t(
        'Od kolika decibelů je ochrana sluchu povinná?',
        'From how many decibels is hearing protection mandatory?',
      ),
      correct: 85,
      tolerance: 0,
    },
    {
      id: 'q-vymena-order',
      kind: 'ordering',
      chapterId: 'ch-po-praci',
      prompt: t('Seřaď výměnu kotouče.', 'Order the disc change.'),
      choices: [
        { id: 'a', label: t('Odpojit od zdroje', 'Disconnect from power') },
        { id: 'b', label: t('Počkat na zastavení', 'Wait for it to stop') },
        { id: 'c', label: t('Vyměnit a zkontrolovat', 'Change and check') },
        { id: 'd', label: t('Odzkoušet naprázdno', 'Test-run unloaded') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
